import React, {memo, useRef} from 'react'
import {ActivityFlightState} from "../../../../types-enums/activity-flight";
import {Icon, TruncatedWordList} from "atmosfair-ui";
import {
    generateDepartureArrivalAirportWithCountryString,
    generateDepartureArrivalRouteString,
    generateProfessionalFlightRouteWithoutCo2KgEStrings
} from "../../../../utils/string/flight";
import {useTranslation} from "react-i18next";
import './styles.css'
import '../styles.css'
import {
    CommonActivityStringProps,
    CommonActivityWithReturnRouteProps,
    DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE,
    ICON_MARGIN
} from "../utils";
import StringDisplayFootnoteIcon, {WHICH_FOOTNOTE_TYPE} from "../common/StringDisplayFootnoteIcon";
import {isDefined, isFlightCalculationRequestLeg} from "../../../../utils/check/type-guards";
import CalculationTierTooltipHint from "../common/CalculationTierTooltipHint";
import {ACTIVITY_ICON_MAPPING} from "../../../../utils/getter/activities";
import {ACTIVITY_TYPE} from "../../../../types-enums/activity";
import {isCo2KgEValueDifferent} from "../../../../utils/check/common";
import {formatCo2ETotalString, WhereToDisplayActivityString} from "../../../../utils/string/common";

interface FlightRouteProps extends CommonActivityStringProps, CommonActivityWithReturnRouteProps {
    flight: ActivityFlightState,
    loading?: boolean,
}

const FlightRoute = memo(function FlightRouteString({
                                                        flight,
                                                        textColor,
                                                        truncatedWordsListMaxWidthPercentage = DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE,
                                                        journeyWithExactDates,
                                                        loading,
                                                        whereToDisplay,
                                                        iconColor,
                                                        hideAllIconHints,
                                                        showReturnIcon
                                                    }: FlightRouteProps
) {
    const {
        legs,
        returnId,
        common: {originalTotalEmissions, request, hideLoadBundleInformation, id},
        passengerCount
    } = flight
    const {i18n, t} = useTranslation()
    const {resolvedLanguage} = i18n
    const {flightRouteString, separator} = generateProfessionalFlightRouteWithoutCo2KgEStrings(legs, i18n)
    const flightHasLegsWithFlightDate = flight.common.request?.some(leg => {
        if (isFlightCalculationRequestLeg(leg)) return leg.flightDate !== null
    })
    const discontinuedAirportsIata = useRef<string[]>([])
    const legsWithDiscontinuedFlightNumber = useRef(legs.reduce((result, leg, index) => {
        const arrivalLeg = legs[index + 1]
        if (
            leg.informationFromLoadedBundle?.flightNumberDiscontinued &&
            arrivalLeg?.informationFromLoadedBundle?.flightNumberDiscontinued
        ) {
            const {airport: departureAirport} = leg
            const {airport: arrivalAirport} = arrivalLeg

            if (departureAirport && arrivalAirport) {
                const {iata: departureIata} = departureAirport
                const {iata: arrivalIata} = arrivalAirport

                if (
                    !discontinuedAirportsIata.current.includes(departureIata) &&
                    !discontinuedAirportsIata.current.includes(arrivalIata)
                ) result.push(leg, arrivalLeg)
            }
        }
        return result
    }, [] as typeof legs))
    const legsWithDifferentAirports = useRef(legs.filter(leg => leg?.informationFromLoadedBundle?.originalAirport))

    const _renderDiscontinuedFlightNumberHint = () => {
        if (legsWithDiscontinuedFlightNumber.current.length === 2) {
            const departureLeg = legsWithDiscontinuedFlightNumber.current[0]
            const arrivalLeg = legsWithDiscontinuedFlightNumber.current[1]
            const {airport: departureAirport} = departureLeg
            const {airport: arrivalAirport} = arrivalLeg
            const {informationFromLoadedBundle} = departureLeg
            if (informationFromLoadedBundle !== null && departureAirport !== null && arrivalAirport !== null) {
                const {flightNumberDiscontinued} = informationFromLoadedBundle
                if (flightNumberDiscontinued !== null) {
                    const {flightNumber, flightDate} = flightNumberDiscontinued
                    return <StringDisplayFootnoteIcon
                        tooltip={[
                            t("HINTS.BUNDLE.SAVED_DATA"),
                            t("HINTS.BUNDLE.FLIGHT_NUMBER_AND_DATE_STRIPPED_OF_LEG", {
                                flightNumber,
                                flightDate,
                                leg: generateDepartureArrivalAirportWithCountryString(departureAirport, arrivalAirport)
                            })
                        ].join(" ")}
                        iconColor={iconColor}
                        hideOnClick={true}
                        activityId={id}
                        whereToDisplay={whereToDisplay}
                        whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
                }
            }
        }

        if (legsWithDiscontinuedFlightNumber.current.length > 2) {
            const stringToDisplay: string[] = []
            let legIndex = 0
            legsWithDiscontinuedFlightNumber.current.forEach((leg, index) => {
                const departureLeg = legsWithDiscontinuedFlightNumber.current[legIndex]
                const arrivalLeg = legsWithDiscontinuedFlightNumber.current[legIndex + 1]
                const {airport: departureAirport} = departureLeg
                const {airport: arrivalAirport} = arrivalLeg
                const {informationFromLoadedBundle} = departureLeg
                if (informationFromLoadedBundle !== null && departureAirport !== null && arrivalAirport !== null) {
                    const {flightNumberDiscontinued} = informationFromLoadedBundle
                    if (flightNumberDiscontinued !== null) {
                        const {flightNumber, flightDate} = flightNumberDiscontinued
                        stringToDisplay.push(`${flightNumber}, ${flightDate} (${generateDepartureArrivalAirportWithCountryString(departureAirport, arrivalAirport)})`)
                    }
                }
                legIndex += 2
            })

            return <StringDisplayFootnoteIcon
                tooltip={[
                    t("HINTS.BUNDLE.SAVED_DATA"),
                    t("HINTS.BUNDLE.FLIGHT_NUMBER_AND_DATE_STRIPPED_OF_LEGS", {legs: stringToDisplay.join(", ")})
                ].join(" ")}
                iconColor={iconColor}
                hideOnClick={true}
                activityId={id}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
        }
    }

    const _renderDifferentCo2KgEValuesOnLoadHint = () => {
        const hintsInformation = legs.map((leg, index) => {
            const {informationFromLoadedBundle} = leg
            if (informationFromLoadedBundle !== null) {
                const {previousEmissionsCo2KgE} = informationFromLoadedBundle
                const currentEmissionsCo2KgEIndex = originalTotalEmissions?.findIndex(emissionOfLeg => emissionOfLeg.index === index)
                if (
                    previousEmissionsCo2KgE !== null &&
                    typeof currentEmissionsCo2KgEIndex !== 'undefined' &&
                    currentEmissionsCo2KgEIndex > -1 &&
                    originalTotalEmissions !== null
                ) {
                    const currentEmissionsCo2KgE = originalTotalEmissions[currentEmissionsCo2KgEIndex].emissionKgCO2e
                    if (
                        request !== null &&
                        currentEmissionsCo2KgE !== null &&
                        isCo2KgEValueDifferent(currentEmissionsCo2KgE, previousEmissionsCo2KgE)
                    ) {
                        const connectionWithDifferentValues = request[index]
                        if (isFlightCalculationRequestLeg(connectionWithDifferentValues)) {
                            const {departure, arrival} = connectionWithDifferentValues
                            if (departure !== null && arrival !== null) return {
                                departure,
                                arrival,
                                previousEmissionsCo2KgE,
                                currentEmissionsCo2KgE
                            }
                        }
                    }
                }
            }
        }).filter(isDefined)
        if (hintsInformation.length === 0) return null

        if (hintsInformation.length === 1 && hintsInformation[0]) {
            const {departure, arrival, previousEmissionsCo2KgE, currentEmissionsCo2KgE} = hintsInformation[0]
            return <StringDisplayFootnoteIcon
                tooltip={t("HINTS.EMISSION_CALCULATION.FLIGHT.DIFFERENT_CALCULATED_CO_KG_E_SINGULAR", {
                    leg: generateDepartureArrivalRouteString(departure, arrival),
                    previousEmissionsCo2KgE: formatCo2ETotalString(previousEmissionsCo2KgE, resolvedLanguage),
                    currentEmissionsCo2KgE: formatCo2ETotalString(currentEmissionsCo2KgE, resolvedLanguage),
                    interpolation: {escapeValue: false}
                })}
                whereToDisplay={whereToDisplay}
                iconColor={iconColor}
                hideOnClick={true}
                activityId={id}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.calculation}/>
        }

        const legsWithInformation = hintsInformation.map(hint => {
            if (hint) {
                const {departure, arrival, previousEmissionsCo2KgE, currentEmissionsCo2KgE} = hint
                return `${generateDepartureArrivalRouteString(departure, arrival)} (${t("GENERAL.OLD")}: ${formatCo2ETotalString(previousEmissionsCo2KgE, resolvedLanguage)}, ${t("GENERAL.NEW")}: ${formatCo2ETotalString(currentEmissionsCo2KgE, resolvedLanguage)})`
            }
        })

        return <StringDisplayFootnoteIcon
            tooltip={t(
                "HINTS.EMISSION_CALCULATION.FLIGHT.DIFFERENT_CALCULATED_CO_KG_E_PLURAL",
                {legsWithValues: legsWithInformation.join(", ")})}
            iconColor={iconColor}
            whereToDisplay={whereToDisplay}
            hideOnClick={true}
            activityId={id}
            whichFootnoteType={WHICH_FOOTNOTE_TYPE.calculation}/>
    }

    const _renderLegsWithDifferentAirportHint = () => {
        let showTooltip = false
        let tooltipText: string[] = [t("HINTS.BUNDLE.SAVED_DATA")]
        if (legsWithDifferentAirports.current.length === 1) {
            const leg = legsWithDifferentAirports.current[0]
            const {airport: newAirport, informationFromLoadedBundle} = leg
            if (informationFromLoadedBundle !== null && newAirport !== null) {
                const {originalAirport} = informationFromLoadedBundle
                if (originalAirport !== null) {
                    const {iata, name: newAirportName, country: newAirportCountry} = newAirport
                    const {name: originalAirportName, country: originalAirportCountry} = originalAirport
                    showTooltip = true
                    tooltipText.push(
                        t("HINTS.BUNDLE.NEW_AIRPORT_UPON_LOADING", {
                            iata,
                            newAirportName,
                            originalAirportName,
                        })
                    )
                    if (newAirportCountry === originalAirportCountry) {
                        showTooltip = true
                        tooltipText.push(t("HINTS.BUNDLE.NEW_AIRPORT_UPON_LOADING_SAME_COUNTRIES", {
                            newAirportCountry,
                            originalAirportCountry,
                        }))
                    } else {
                        showTooltip = true
                        tooltipText.push(t("HINTS.BUNDLE.NEW_AIRPORT_UPON_LOADING_DIFFERENT_COUNTRIES", {
                            newAirportCountry,
                            originalAirportCountry,
                        }))
                    }
                }
            }
        }
        if (legsWithDifferentAirports.current.length > 1) {
            const originalAirportNames: string[] = []
            const newAirportNames: string[] = []
            const iatas: string[] = []
            const originalAirportCountries: string[] = []
            const newAirportCountries: string[] = []
            legsWithDifferentAirports.current.forEach(leg => {
                const {airport: newAirport, informationFromLoadedBundle} = leg
                if (informationFromLoadedBundle !== null && newAirport !== null) {
                    const {originalAirport} = informationFromLoadedBundle
                    if (originalAirport !== null) {
                        const {iata, name: newAirportName, country: newAirportCountry} = newAirport
                        const {name: originalAirportName, country: originalAirportCountry} = originalAirport
                        originalAirportNames.push(originalAirportName)
                        newAirportNames.push(newAirportName)
                        iatas.push(iata)
                        originalAirportCountries.push(originalAirportCountry)
                        newAirportCountries.push(newAirportCountry)
                    }
                }
            })
            showTooltip = true
            tooltipText.push(
                t("HINTS.BUNDLE.NEW_AIRPORTS_UPON_LOADING", {
                    iatas: iatas.join(", "),
                    newAirportNames: newAirportNames.join(", "),
                    originalAirportNames: originalAirportNames.join(", "),
                })
            )
            if (originalAirportNames.some((originalAirportName, index) => originalAirportName !== newAirportNames[index])) {
                showTooltip = true
                tooltipText.push(
                    t("HINTS.BUNDLE.NEW_AIRPORTS_UPON_LOADING_DIFFERENT_COUNTRIES", {
                        originalAirportCountries: originalAirportCountries.join(", "),
                        newAirportCountries: newAirportCountries.join(", ")
                    })
                )
            } else {
                showTooltip = true
                tooltipText.push(
                    t("HINTS.BUNDLE.NEW_AIRPORTS_UPON_LOADING_SAME_COUNTRIES", {
                        originalAirportCountries: originalAirportCountries.join(", "),
                        newAirportCountries: newAirportCountries.join(", ")
                    })
                )
            }
        }
        if (showTooltip) return <StringDisplayFootnoteIcon
            tooltip={tooltipText.join(" ")}
            iconColor={iconColor}
            hideOnClick={true}
            activityId={id}
            whereToDisplay={whereToDisplay}
            whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
    }

    return <div
        className={'activity-string-display-container'}
        style={{maxWidth: `${truncatedWordsListMaxWidthPercentage}%`}}>
        <Icon
            containerClasses={"activity-string-display-activity-icon-margin"}
            size={"small"}
            color={iconColor}
            icon={ACTIVITY_ICON_MAPPING[ACTIVITY_TYPE.flight]}/>
        <TruncatedWordList color={textColor} separator={separator}>
            {`${passengerCount} ${passengerCount === 1 ? t("GENERAL.PERSON") : t("GENERAL.PERSONS")}, ${flightRouteString}`}
        </TruncatedWordList>
        {
            returnId &&
            showReturnIcon &&
            <Icon
                color={iconColor}
                iconStyle={ICON_MARGIN}
                icon={"repeat"}
                size={'verySmall'}
                tooltip={'TOOLTIP.ACTIVITY_FLIGHT.ROUNDTRIP_SINGULAR'}/>}
        {
            !loading &&
            journeyWithExactDates &&
            flightHasLegsWithFlightDate &&
            <StringDisplayFootnoteIcon
                tooltip={t("HINTS.JOURNEY.FLIGHT_CO2_KG_E_DEPENDS_ON_DATE")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
        }
        {_renderLegsWithDifferentAirportHint()}
        {_renderDiscontinuedFlightNumberHint()}
        {
            whereToDisplay === WhereToDisplayActivityString.journey &&
            flightHasLegsWithFlightDate &&
            !journeyWithExactDates &&
            <StringDisplayFootnoteIcon
                tooltip={t("HINTS.JOURNEY.FLIGHT_WITH_FLIGHT_NUMBER_JOURNEY_WITHOUT_DATES")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.date}/>
        }
        {
            !hideAllIconHints &&
            !hideLoadBundleInformation &&
            _renderDifferentCo2KgEValuesOnLoadHint()
        }
        <CalculationTierTooltipHint iconColor={iconColor} activity={flight}/>
    </div>
})

export default FlightRoute